import styled, { css, keyframes } from "styled-components";

/* fade + up subtil pentru text */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const CarouselWrap = styled.section`
  position: relative;
  height: var(--hero-h, 70vh);
  min-height: 420px;
  overflow: hidden;
  color: #fff;
  user-select: none;
  --accent: var(--accent, #a78631);
`;

export const Slide = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0;
  transform: scale(1.01);
  transition: opacity 700ms ease, transform 900ms ease;
  ${(p) =>
    p.$active &&
    css`
      opacity: 1;
      transform: scale(1);
      z-index: 1;
    `}
`;

export const Image = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.9) saturate(1.02);

  /* 🎯 Focus mai jos – se vede mai mult jumătatea de jos */
  object-position: center 80%;

  @media (max-width: 560px) {
    /* Pe mobil revenim puțin spre centru */
    object-position: center 90%;
  }
`;

export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
      80% 60% at 50% 50%,
      rgba(0, 0, 0, 0.45),
      transparent 70%
    ),
    linear-gradient(
      to top,
      rgba(0, 0, 0, 0.8) 0%,
      rgba(0, 0, 0, 0.55) 35%,
      rgba(0, 0, 0, 0.25) 75%,
      transparent 100%
    );
  box-shadow: inset 0 -140px 180px rgba(0, 0, 0, 0.55),
    inset 0 60px 120px rgba(0, 0, 0, 0.25);

  @media (max-width: 560px) {
    /* 📱 Pe mobil – ușor mai intensă sus, dar echilibrată */
    background: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.75) 0%,
        /* 🔥 un pic mai intens (de la 0.7 → 0.75) */ rgba(0, 0, 0, 0.5) 25%,
        /* mai clar contrast în zona textului */ rgba(0, 0, 0, 0.3) 55%,
        rgba(0, 0, 0, 0.15) 85%,
        transparent 100%
      ),
      radial-gradient(
        80% 60% at 50% 30%,
        rgba(0, 0, 0, 0.45),
        /* 🔥 mai densă (de la 0.4 → 0.45) */ transparent 70%
      );
    box-shadow: inset 0 75px 115px rgba(0, 0, 0, 0.5); /* puțin mai accentuat */
  }
`;

export const Caption = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  display: grid;
  text-align: center;
  padding: 0 20px;
  animation: ${fadeUp} 0.9s ease both;

  /* 💻 Desktop – text jos */
  align-content: end;
  padding-bottom: 90px;

  @media (max-width: 560px) {
    /* 📱 Pe mobil – mai sus și mai aerisit */
    align-content: start;
    justify-items: center;
    padding-top: 30px; /* <- era 80px, acum puțin mai sus */
    padding-bottom: 0;
    gap: 10px;
  }
`;

export const Title = styled.h1`
  margin: 0;
  line-height: 1.12;
  color: #fff;
  font-weight: 800;
  font-size: clamp(28px, 5.4vw, 46px);
  letter-spacing: 0.2px;
  text-wrap: balance;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.55);

  &::after {
    content: "";
    display: block;
    width: 84px;
    height: 2px;
    margin: 12px auto 0;
    background: var(--accent);
    border-radius: 2px;
    opacity: 0.9;
  }

  @media (max-width: 360px) {
    font-size: 23px;
  }
`;

export const Subtitle = styled.p`
  margin: 14px auto 0;
  max-width: 760px;
  font-size: clamp(14px, 2.1vw, 20px);
  font-weight: 600;
  color: var(--accent);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);

  @media (max-width: 420px) {
    font-size: 13.5px;
    font-weight: 600;
    opacity: 0.95;
  }
  @media (max-width: 340px) {
    display: none; /* pe telefoane foarte mici păstrăm doar titlul */
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 18px;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 560px) {
    width: 100%;
    gap: 8px;
    margin-top: 14px;
  }
`;

/* butoane discrete, elegante */
export const CTAButton = styled.button`
  appearance: none;
  border: 0;
  cursor: pointer;
  padding: 10px 22px;
  border-radius: 999px;
  font-weight: 600;
  letter-spacing: 0.2px;
  font-size: 14.5px;
  min-height: 38px;
  line-height: 1.3;
  transition: transform 0.14s ease, background 0.18s ease, color 0.18s ease,
    border-color 0.18s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);

  ${(p) =>
    p["data-variant"] === "primary"
      ? css`
          background: transparent;
          color: #fff;
          border: 1px solid color-mix(in srgb, var(--accent) 88%, #fff 12%);
          &:hover {
            background: color-mix(in srgb, var(--accent) 16%, #000 84%);
            transform: translateY(-1px);
          }
        `
      : css`
          background: transparent;
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.45);
          &:hover {
            background: rgba(255, 255, 255, 0.14);
            transform: translateY(-1px);
          }
        `}

  @media (max-width: 560px) {
    /* pe mobil — ambele sunt „ghost” */
    padding: 9px 16px;
    font-size: 14px;
    min-height: 34px;
    border-width: 1px;
  }
`;

export const NavBtn = styled.button`
  position: absolute;
  bottom: 22px;
  ${(p) => p.left && "left: 18px;"} ${(p) => p.right && "right: 18px;"}
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: none;
  background: rgba(17, 17, 17, 0.5);
  color: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
  z-index: 3;
  transition: background 0.2s ease, transform 0.2s ease;
  backdrop-filter: blur(3px);
  &:hover {
    background: rgba(17, 17, 17, 0.7);
    transform: scale(1.06);
  }

  @media (max-width: 560px) {
    width: 32px;
    height: 32px;
    bottom: calc(14px + env(safe-area-inset-bottom));
  }
`;

export const Dots = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(8px + env(safe-area-inset-bottom));
  display: flex;
  justify-content: center;
  gap: 8px;
  z-index: 3;
`;

export const Dot = styled.button`
  width: ${(p) => (p.$active ? "12px" : "8px")};
  height: 8px;
  border-radius: 999px;
  border: 0;
  background: ${(p) => (p.$active ? "var(--accent)" : "rgba(255,255,255,.48)")};
  cursor: pointer;
  transition: all 0.22s ease;
  opacity: ${(p) => (p.$active ? 1 : 0.8)};
`;
